import { useQuery } from '@tanstack/react-query';
import { collection, doc, getDoc, getDocs, orderBy, query } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { Part } from '@/types/Part';

/**
 * TanStack Query-based reads for customer-facing inventory views.
 * Admin pages still use the onSnapshot-based useParts hook because
 * they benefit from real-time updates after edits.
 */

const PARTS_KEY = ['parts', 'customer'] as const;

async function fetchLiveParts(): Promise<Part[]> {
  // Only `live` and `reserved` parts surface to subscribers; drafts
  // and sold parts are filtered out client-side. We could pre-filter
  // with a Firestore where(), but in/in queries with orderBy can
  // require composite indexes, and the catalog is small.
  const snap = await getDocs(
    query(collection(db, 'parts'), orderBy('updatedAt', 'desc'))
  );
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() } as Part))
    .filter((p) => p.status === 'live' || p.status === 'reserved');
}

async function fetchPartById(id: string): Promise<Part | null> {
  const snap = await getDoc(doc(db, 'parts', id));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() } as Part;
}

export function useCustomerParts() {
  return useQuery({
    queryKey: PARTS_KEY,
    queryFn: fetchLiveParts,
    staleTime: 60_000, // 1 min — inventory doesn't change every second
  });
}

export function usePart(id: string | undefined) {
  return useQuery({
    queryKey: ['parts', 'detail', id],
    queryFn: () => (id ? fetchPartById(id) : Promise.resolve(null)),
    enabled: !!id,
    staleTime: 60_000,
  });
}
